import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

interface PromoPackActivationDialogProps {
  open: boolean;
  /** The pack the admin is turning on. */
  packName?: string;
  /** Undefined means the pack is aimed at every market. */
  territory?: string;
  /** The promo pack(s) that will be switched off by this activation. */
  replacing: { _id: string; name: string }[];
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const useStyles = makeStyles((theme: any) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 8,
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      maxWidth: 560,
    },
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: 12,
  },
  dialogContent: {
    padding: 16,
  },
  packName: {
    fontWeight: 700,
  },
  list: {
    margin: "8px 0 0 0",
    paddingLeft: 20,
  },
  note: {
    marginTop: 12,
    fontSize: 13,
    color: "#666",
  },
  dialogActions: {
    padding: 15,
  },
  cancelButton: {
    color: theme.palette.text.secondary,
  },
}));

// Turning on a promo pack turns off the other one aimed at the same market. Names which pack loses
// its Active status before that happens.
const PromoPackActivationDialog = ({
  open,
  packName,
  territory,
  replacing,
  loading = false,
  onClose,
  onConfirm,
}: PromoPackActivationDialogProps) => {
  const classes = useStyles();
  const marketLabel = territory && territory !== "all" ? territory : "All markets";

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        Only one Promo (Star Player) pack can be active per territory
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography>
          Activating{" "}
          <span className={classes.packName}>{packName || "this pack"}</span>{" "}
          for <span className={classes.packName}>{marketLabel}</span> will
          switch off the Promo (Star Player) pack active in that territory now:
        </Typography>
        <ul className={classes.list}>
          {replacing.map((pack) => (
            <li key={pack._id}>
              <Typography component="span" className={classes.packName}>
                {pack.name}
              </Typography>{" "}
              <Typography component="span" style={{ color: "#666" }}>
                ({pack._id})
              </Typography>
            </li>
          ))}
        </ul>
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          A player who has already been shown the four cards keeps those exact
          cards and still claims one of them, even after reopening the game.
          Only players who have not been shown a pack yet are offered{" "}
          {packName || "this pack"}.
        </Alert>
        <Typography className={classes.note}>
          The pack being switched off keeps all of its settings and can be
          activated again later. Promo packs for other territories are not
          affected.
        </Typography>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          autoFocus
          disabled={loading}
        >
          Activate anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoPackActivationDialog;
